/** @format */

import {
	compareQuestionNumbers,
	getQuestionContentBlocks,
	hasDisplayableContent,
	type InheritedPaperFields,
	type ParentGroupSummary,
	type Question,
	type QuestionSpecification,
} from "@question-bank/shared";

/**
 * Build a {@link ParentGroupSummary} from a ribbon group's questions.
 *
 * The paper-level facts (`subject`, `qualification`, `tier`, `series`, `date`)
 * no longer live on a bare {@link Question} (QB2-223); they belong to the owning
 * Paper.
 * The caller resolves that Paper and passes its fields via `paperFields`. When
 * `paperFields` is omitted (e.g. the paper has not loaded), the summary falls
 * back to empty placeholders so the group still renders.
 */
export const buildParentGroupSummary = (items: Question[], paperFields?: InheritedPaperFields): ParentGroupSummary => {
	const sorted = [...items].sort((a, b) => compareQuestionNumbers(a.questionNumber, b.questionNumber));
	const root = sorted.find((q) => q.isRoot);
	const representative = root ?? sorted[0];
	const hasOwnRow = root !== undefined;

	let totalMarks = 0;
	const specMap = new Map<string, QuestionSpecification>();
	let aoTotal: { ao1: number; ao2: number; ao3: number } | undefined;

	for (const item of items) {
		if (typeof item.maxMarks === "number") {
			totalMarks += item.maxMarks;
		}
		if (item.specification) {
			const key = JSON.stringify([
				item.specification.theme ?? "",
				item.specification.topic ?? "",
				item.specification.subTopic ?? "",
			]);
			if (!specMap.has(key)) {
				specMap.set(key, item.specification);
			}
		}
		if (item.assessmentObjectives) {
			aoTotal ??= { ao1: 0, ao2: 0, ao3: 0 };
			aoTotal.ao1 += item.assessmentObjectives.ao1 ?? 0;
			aoTotal.ao2 += item.assessmentObjectives.ao2 ?? 0;
			aoTotal.ao3 += item.assessmentObjectives.ao3 ?? 0;
		}
	}

	const contentSource = sorted.find((q) => getQuestionContentBlocks(q).length > 0);

	const summary: ParentGroupSummary = {
		parentQuestionId: representative.parentQuestionId,
		rootQuestionNumber: representative.rootQuestionNumber,
		questionNumber: representative.rootQuestionNumber,
		paperCode: representative.paperCode,
		paperId: representative.paperId,
		series: paperFields?.series ?? { season: { code: "" }, year: 0 },
		subject: paperFields?.subject ?? "",
		qualification: paperFields?.qualification ?? { code: "" },
		tier: paperFields?.tier,
		language: representative.language,
		brand: representative.brand,
		status: representative.status,
		date: paperFields?.date ?? "",
		childCount: items.filter(hasDisplayableContent).length,
		totalMarks,
		specifications: Array.from(specMap.values()),
		hasOwnRow,
	};
	if (contentSource) {
		const blocks = getQuestionContentBlocks(contentSource);
		if (blocks.length > 0) {
			summary.contentBlocks = blocks;
		}
		if (contentSource.en) {
			summary.en = contentSource.en;
		}
		if (contentSource.cy) {
			summary.cy = contentSource.cy;
		}
	}
	if (aoTotal) {
		summary.assessmentObjectives = {
			...(aoTotal.ao1 > 0 ? { ao1: aoTotal.ao1 } : {}),
			...(aoTotal.ao2 > 0 ? { ao2: aoTotal.ao2 } : {}),
			...(aoTotal.ao3 > 0 ? { ao3: aoTotal.ao3 } : {}),
		};
	}
	return summary;
};
